package com.arena_web.ui.sections.news

import androidx.compose.runtime.*
import com.arena_web.data.dto.FootballMatchDto
import com.arena_web.data.dto.MatchDetailDto
import com.arena_web.data.dto.NewsMatchLinkDto
import com.arena_web.di.LocalAppContainer
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppGradients
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText

// =============================================================================
// Linked-match block on the article page (/news/{id}) — spec:
// docs/superpowers/specs/2026-09-21-news-match-link-design.md. The card paints
// instantly from the article's snapshot; getMatchDetail then upgrades score/
// status and brings the highlight embed when TSDB has the video. A failed
// fetch just leaves the snapshot card (no error/loading UI — convention).
// =============================================================================

@Composable
fun ArticleMatchBlock(link: NewsMatchLinkDto, mobile: Boolean) {
    val t = LocalAppStrings.current
    val ctx = rememberPageContext()
    val repository = LocalAppContainer.current.newsRepository
    var detail by remember(link.id) { mutableStateOf<MatchDetailDto?>(null) }

    LaunchedEffect(link.id) {
        repository.getMatchDetail(
            sport = link.sport,
            id = link.id,
            date = link.utcDate.take(10),
            competition = link.competitionCode,
            home = link.homeTeam,
            away = link.awayTeam,
        ).onSuccess { detail = it }
    }

    detail?.video?.takeIf { it.isNotBlank() }?.let { video ->
        SectionHeader(t.newsArticleHighlights, mobile)
        HighlightEmbed(video)
    }

    SectionHeader(t.newsArticleMatch, mobile)
    val match = FootballMatchDto(
        id = link.id,
        competitionCode = link.competitionCode,
        competitionName = link.competitionName,
        utcDate = link.utcDate,
        status = detail?.status ?: "SCHEDULED",
        homeTeam = link.homeTeam,
        homeCrest = link.homeCrest,
        awayTeam = link.awayTeam,
        awayCrest = link.awayCrest,
        homeScore = detail?.homeScore,
        awayScore = detail?.awayScore,
    )
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .then(if (mobile) Modifier.mBorderRadius(20) else Modifier.sBorderRadius(22))
            .overflow(Overflow.Hidden)
            .styleModifier {
                property("border", "0.5px solid ${AppGradients.NewsCardBorder}")
                property("background", "var(--l-surface-2, var(--l-s2, #09142D))")
            }
    ) {
        if (link.competitionName.isNotBlank()) {
            SpanText(
                link.competitionName,
                modifier = Modifier
                    .fillMaxWidth()
                    .then(if (mobile) Modifier.mFontSize(12).mPadding(10, 14) else Modifier.sFontSize(13).sPadding(12, 20))
                    .color(com.arena_web.ui.theme.ThemeVars.Text3)
                    .styleModifier { property("background", "rgba(60, 121, 241, 0.06)") }
            )
        }
        MatchRow(match, mobile) { openMatchPage(ctx, link.sport, match) }
    }
}
